import { Component, computed, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { Planning } from 'src/app/classes/planning.class';
import { CodaltComponent } from 'src/app/codalt.component';
import { PlanningDetailContainerComponent } from 'src/app/pages/planning-overview/planning-detail-container/planning-detail-container.component';
import { ProjectEditDialogComponent } from 'src/app/pages/projects/project-edit-dialog/project-edit-dialog.component';
import { CodaltModusService } from 'src/app/services/codalt-modus.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog-service/confirm-dialog.service';
import { PlanningService } from 'src/app/services/planning/planning.service';
import { TracklinePlanningEditDialogComponent } from 'src/app/trackline/pages/trackline-planning-edit-dialog/trackline-planning-edit-dialog.component';

export enum PeriodPreset {
    ThreeWeeks = '3weeks',
    RecentlyUpdated = 'recently-updated',
}

const PLANNING_DIALOG_COMPONENTS: any = {
    trackline: TracklinePlanningEditDialogComponent,
    dvds: PlanningDetailContainerComponent,
    buiten_gewoon: PlanningDetailContainerComponent,
} as const;

const PLANNING_DIALOG_DATA_MAPPERS: any = {
    dvds: (planning: Planning) => ({ ...planning }),
    trackline: (planning: Planning) => ({ planning }),
} as const;

@Component({
    selector: 'app-planning-table',
    standalone: false,
    templateUrl: './planning-table.component.html',
    styleUrl: './planning-table.component.scss',
})
export class PlanningTableComponent extends CodaltComponent implements OnInit {
    readonly PeriodPreset = PeriodPreset;

    readonly presets: { label: string; value: PeriodPreset }[] = [
        { label: '3 weken', value: PeriodPreset.ThreeWeeks },
        { label: 'Recent aangepast', value: PeriodPreset.RecentlyUpdated },
    ];

    readonly activePreset = signal<PeriodPreset | null>(PeriodPreset.ThreeWeeks);
    readonly selectedDate = signal<Date | null>(null);

    readonly items = signal<any[]>([]);
    readonly loading = signal(true);

    @Input() title = '';

    @Input() userId: number | undefined = undefined;
    @Input() entityId: number | undefined = undefined;
    @Input() projectId: string | undefined = undefined;
    @Output() planningClicked = new EventEmitter<number>();
    @Output() projectClicked = new EventEmitter<string>();

    public codaltModusService = inject(CodaltModusService);
    private planningService = inject(PlanningService);
    private confirmDialogService = inject(ConfirmDialogService);
    private dialog = inject(MatDialog);

    private readonly apiParams = computed(() => {
        const preset = this.activePreset();
        const date = this.selectedDate();

        if (preset === PeriodPreset.RecentlyUpdated) {
            return { recently_updated: true };
        }

        let from: string | undefined;
        let to: string | undefined;

        if (preset === PeriodPreset.ThreeWeeks) {
            from = this.prevMonday().toISOString();
            to = this.nextSunday().toISOString();
        } else if (date) {
            from = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0).toISOString();
            to = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999).toISOString();
        }

        return { from, to };
    });

    private readonly apiParams$ = toObservable(this.apiParams);

    setPreset(preset: PeriodPreset) {
        this.activePreset.set(preset);
        this.selectedDate.set(null);
    }

    onDateSelected(date: Date | null) {
        this.selectedDate.set(date);
        if (date) {
            this.activePreset.set(null);
        }
    }

    ngOnInit(): void {
        this.subscriptions.add(
            this.apiParams$
                .pipe(
                    switchMap(params => {
                        this.loading.set(true);
                        if (!this.codaltModusService.enabled()) {
                            return of([]);
                        }
                        return this.codaltModusService.getPlanningDetails({
                            user_id: this.userId,
                            entity_id: this.entityId,
                            project_id: this.projectId,
                            ...params,
                        });
                    })
                )
                .subscribe(result => {
                    this.loading.set(false);
                    this.items.set(result);
                })
        );
    }

    onProjectClicked(afasProjectId: string, event: MouseEvent) {
        event.stopPropagation();
        this.dialog.open(ProjectEditDialogComponent, {
            data: { projectId: afasProjectId },
            width: '99vw',
            minWidth: '99vw',
        });
    }

    onPlanningClicked(planningId: number) {
        this.subscriptions.add(
            this.planningService
                .getPlanningFull(planningId, true)
                .pipe(
                    filter((planning: Planning) => !!planning.id),
                    map((planning: Planning) => {
                        const componentKey = this.environment.planningTableDialog;

                        return {
                            planning,
                            component: PLANNING_DIALOG_COMPONENTS[componentKey],
                            data: PLANNING_DIALOG_DATA_MAPPERS[componentKey](planning),
                        };
                    })
                )
                .subscribe({
                    next: ({ planning, component, data }) => {
                        if (!planning.planning_has_id) {
                            this.confirmDialogService.confirm({
                                title: 'Er is een fout opgetreden',
                                confirm: 'Sluiten',
                                text: 'Deze planning kon niet geopend worden.',
                            });

                            return;
                        }

                        this.dialog.open(component, {
                            disableClose: true,
                            panelClass: 'planning-edit-dialog',
                            data,
                        });
                    },
                })
        );
    }

    private prevMonday(): Date {
        const d = new Date();
        const day = d.getDay() || 7;
        d.setDate(d.getDate() - (day - 1) - 7);
        d.setHours(0, 0, 0, 0);
        return d;
    }

    private nextSunday(): Date {
        const d = new Date();
        const day = d.getDay() || 7;
        d.setDate(d.getDate() + (7 - day) + 7);
        d.setHours(23, 59, 59, 999);
        return d;
    }
}
